import { privateHttp } from "~/config/axios";
import { API_ENDPOINTS } from "~/config/endpoints";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { BaseApiResponse } from "~/types/common";

/* ─── 타입 ─── */
export interface DiaryStyleStatus {
  registered: boolean;
  styleReady: boolean;
  sampleCount: number;
  dtFirstLearned: string | null;
  dtLearned: string | null;
  defaultPresetCode: string;
}

export interface DiaryStylePreset {
  code: string;
  name: string;
  description: string;
}

export interface DiaryStyleSample {
  sampleSeq: number;
  kind: "IMAGE" | "TEXT" | "FILE";
  fileUrl: string | null;
  textPreview: string | null;
  dtRegist: string | null;
}

export interface DiaryStyleNotice {
  noticeSeq: number;
  petName: string | null;
  preview: string | null;
  dtRegist: string | null;
}

export interface DiaryActivities {
  meal?: string;
  snack?: string;
  water?: string;
  poop?: string;
  activityLevel?: string;
}

export interface DiaryGenerateRequest {
  petSeq: number;
  reservationSeq?: number | null;
  imageFileSeqs?: number[];
  videoFileSeqs?: number[];
  memo?: string;
  activityNote?: string;
}

export interface DiaryGenerateResponse {
  callSeq: number;
  body: string;
  highlight?: string;
  activities?: DiaryActivities;
  stubbedYn: "Y" | "N";
  creditCharged: number;
}

const STATUS_KEY = ["business-diary-style-status"] as const;
const PRESETS_KEY = ["business-diary-style-presets"] as const;
const SAMPLES_KEY = ["business-diary-style-samples"] as const;

const unwrapSuccess = <T>(response: BaseApiResponse<T>, action: string): T => {
  if (!response || response.statusCode !== 200) {
    throw new Error(
      response?.message ||
        `${action} 실패 (statusCode=${response?.statusCode ?? "unknown"})`,
    );
  }
  return response.data as T;
};

/* ─── 스타일 상태 ─── */
const fetchDiaryStyleStatus = async (): Promise<DiaryStyleStatus> => {
  const { data } = await privateHttp.get<BaseApiResponse<DiaryStyleStatus>>(
    API_ENDPOINTS.BUSINESS_DIARY.STYLE_STATUS,
  );
  return unwrapSuccess(data, "알림장 스타일 상태 조회");
};

export const useDiaryStyleStatus = () =>
  useQuery({ queryKey: STATUS_KEY, queryFn: fetchDiaryStyleStatus });

/* ─── 프리셋 목록 ─── */
const fetchDiaryStylePresets = async (): Promise<DiaryStylePreset[]> => {
  const { data } = await privateHttp.get<BaseApiResponse<DiaryStylePreset[]>>(
    API_ENDPOINTS.BUSINESS_DIARY.STYLE_PRESETS,
  );
  return unwrapSuccess(data, "알림장 스타일 프리셋 조회") ?? [];
};

export const useDiaryStylePresets = () =>
  useQuery({ queryKey: PRESETS_KEY, queryFn: fetchDiaryStylePresets });

/* ─── 샘플 목록 ─── */
const fetchDiaryStyleSamples = async (): Promise<DiaryStyleSample[]> => {
  const { data } = await privateHttp.get<BaseApiResponse<DiaryStyleSample[]>>(
    API_ENDPOINTS.BUSINESS_DIARY.STYLE_SAMPLES,
  );
  return unwrapSuccess(data, "알림장 스타일 샘플 조회") ?? [];
};

export const useDiaryStyleSamples = () =>
  useQuery({ queryKey: SAMPLES_KEY, queryFn: fetchDiaryStyleSamples });

/* ─── 기존 알림장 후보 (스타일 취합용) ─── */
const NOTICES_KEY = ["business-diary-style-notices"] as const;

const fetchDiaryStyleNotices = async (): Promise<DiaryStyleNotice[]> => {
  const { data } = await privateHttp.get<BaseApiResponse<DiaryStyleNotice[]>>(
    API_ENDPOINTS.BUSINESS_DIARY.STYLE_NOTICES,
  );
  return unwrapSuccess(data, "기존 알림장 조회") ?? [];
};

export const useDiaryStyleNotices = (enabled = true) =>
  useQuery({
    queryKey: NOTICES_KEY,
    queryFn: fetchDiaryStyleNotices,
    enabled,
  });

/* ─── 학습(등록/추가/다시학습) ─── */
export interface LearnDiaryStyleParams {
  mode: "APPEND" | "RESET";
  files?: File[];
  texts?: string[];
  noticeSeqs?: number[];
}

export const useLearnDiaryStyle = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (
      params: LearnDiaryStyleParams,
    ): Promise<DiaryStyleStatus> => {
      const fd = new FormData();
      fd.append("mode", params.mode);
      (params.files ?? []).forEach((f) => fd.append("files", f));
      (params.texts ?? []).forEach((t) => fd.append("texts", t));
      (params.noticeSeqs ?? []).forEach((s) =>
        fd.append("noticeSeqs", String(s)),
      );
      const { data } = await privateHttp.post<
        BaseApiResponse<DiaryStyleStatus>
      >(API_ENDPOINTS.BUSINESS_DIARY.STYLE_LEARN, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return unwrapSuccess(data, "알림장 스타일 학습");
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: STATUS_KEY });
      qc.invalidateQueries({ queryKey: SAMPLES_KEY });
    },
  });
};

/* ─── 샘플 삭제 ─── */
export const useDeleteDiaryStyleSample = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (sampleSeq: number): Promise<void> => {
      const { data } = await privateHttp.delete<BaseApiResponse<void>>(
        API_ENDPOINTS.BUSINESS_DIARY.STYLE_SAMPLE_DELETE(sampleSeq),
      );
      unwrapSuccess(data, "알림장 스타일 샘플 삭제");
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: STATUS_KEY });
      qc.invalidateQueries({ queryKey: SAMPLES_KEY });
    },
  });
};

/* ─── 초안 생성 (단일 펫) ─── */
export const useGenerateDiary = () =>
  useMutation({
    mutationFn: async (
      req: DiaryGenerateRequest,
    ): Promise<DiaryGenerateResponse> => {
      const { data } = await privateHttp.post<
        BaseApiResponse<DiaryGenerateResponse>
      >(API_ENDPOINTS.BUSINESS_DIARY.GENERATE, req);
      return unwrapSuccess(data, "AI 알림장 생성");
    },
  });

/* ─── 텍스트 다듬기 ─── */
export const usePolishDiary = () =>
  useMutation({
    mutationFn: async (text: string): Promise<DiaryGenerateResponse> => {
      const { data } = await privateHttp.post<
        BaseApiResponse<DiaryGenerateResponse>
      >(API_ENDPOINTS.BUSINESS_DIARY.POLISH, { text });
      return unwrapSuccess(data, "AI 알림장 다듬기");
    },
  });

/* ─── 일괄 생성 (SSE) ─── */
export interface DiaryBatchItem {
  petSeq: number;
  reservationSeq?: number | null;
  imageFileSeqs?: number[];
  videoFileSeqs?: number[];
  memo?: string;
  activityNote?: string;
}

export interface DiaryBatchEvent {
  index: number;
  petSeq: number;
  code: string; // ResponseCodeEnum name (예: COMPLETE)
  data?: DiaryGenerateResponse;
}

export interface DiaryBatchCallbacks {
  onStart?: (total: number) => void;
  onItemStart?: (petSeq: number) => void; // 펫 처리 시작(미디어 추출 등) — 진행 중 반영용
  onDiary?: (ev: DiaryBatchEvent) => void;
  onComplete?: (done: number) => void;
  onError?: (message: string) => void;
}

/**
 * 오늘 등원 펫 일괄 생성 — SSE(POST + ReadableStream).
 * EventSource 가 POST 를 못 쓰므로 raw fetch 로 스트림을 읽는다(계약 임포트와 동일 패턴).
 */
export async function streamDiaryBatch(
  items: DiaryBatchItem[],
  cb: DiaryBatchCallbacks,
): Promise<void> {
  const base = import.meta.env.VITE_API_BASE_URL ?? ""; // 이미 /api 포함
  const token = localStorage.getItem("accessToken") ?? "";
  const res = await fetch(`${base}${API_ENDPOINTS.BUSINESS_DIARY.BATCH}`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ items }),
  });
  if (!res.ok || !res.body) {
    throw new Error(`일괄 생성 스트림 실패 (status=${res.status})`);
  }
  const reader = res.body.getReader();
  const decoder = new TextDecoder("utf-8");
  let buffer = "";

  const processBuffer = () => {
    let idx: number;
    while ((idx = buffer.indexOf("\n\n")) !== -1) {
      const block = buffer.slice(0, idx);
      buffer = buffer.slice(idx + 2);
      let event = "";
      let data = "";
      for (const line of block.split("\n")) {
        if (line.startsWith("event:")) event = line.slice(6).trim();
        else if (line.startsWith("data:")) data += line.slice(5).trim();
      }
      if (!event) continue;
      let parsed: any = {};
      try {
        parsed = data ? JSON.parse(data) : {};
      } catch {
        continue;
      }
      if (event === "start") cb.onStart?.(parsed.total ?? 0);
      else if (event === "itemStart") cb.onItemStart?.(parsed.petSeq ?? 0);
      else if (event === "diary") cb.onDiary?.(parsed as DiaryBatchEvent);
      else if (event === "complete") cb.onComplete?.(parsed.done ?? 0);
      else if (event === "error") cb.onError?.(parsed.message ?? "오류");
    }
  };

  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    processBuffer();
  }
  processBuffer();
}
